/* Admin Security Health dashboard — 2FA adoption, suspicious logins, abuse. */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { ShieldCheck, AlertTriangle, UserX, Globe, Zap, RefreshCw } from "lucide-react";

function KpiCard({ label, value, hint, accent = "sky", testId }) {
  const palette = {
    sky:     "border-sky-200 bg-sky-50 text-sky-900",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    amber:   "border-amber-200 bg-amber-50 text-amber-900",
    rose:    "border-rose-200 bg-rose-50 text-rose-900",
    indigo:  "border-indigo-200 bg-indigo-50 text-indigo-900",
  }[accent];
  return (
    <div className={`rounded-xl border p-4 ${palette}`} data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
      {hint && <div className="text-[11px] opacity-80 mt-1">{hint}</div>}
    </div>
  );
}

export default function AdminSecurityPanel() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const bg = language === "bg";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // NEW v2: flagged IPs/users with geolocation
  const [flaggedIps, setFlaggedIps] = useState([]);
  const [flaggedUsers, setFlaggedUsers] = useState([]);
  const [abuseLoading, setAbuseLoading] = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get(`${API}/security/admin/health`);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || (bg ? "Грешка при зареждане" : "Failed to load"));
    } finally { setLoading(false); }
  };

  const loadAbuse = async () => {
    setAbuseLoading(true);
    try {
      const [ipsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/security/flagged-ips?days=7`),
        axios.get(`${API}/admin/security/flagged-users?days=7`),
      ]);
      setFlaggedIps(ipsRes.data?.flagged_ips || []);
      setFlaggedUsers(usersRes.data?.flagged_users || []);
    } catch {
      /* silent */
    } finally {
      setAbuseLoading(false);
    }
  };

  useEffect(() => { load(); loadAbuse(); /* eslint-disable-next-line */ }, []);

  if (loading) return <div className="p-6 text-sm text-gray-500">{bg ? "Зареждане…" : "Loading…"}</div>;
  if (error) return <div className="p-6 text-sm text-rose-600">{error}</div>;
  if (!data) return null;

  const u = data.users || {};
  const l = data.logins_30d || {};

  return (
    <div className="space-y-6" data-testid="admin-security-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheck size={20} className="text-sky-600" />
            {bg ? "Здравословно състояние на сигурността" : "Security Health"}
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {bg ? "Генерирано на " : "Generated at "}{new Date(data.generated_at).toLocaleString()}
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-1 text-xs text-sky-700 hover:bg-sky-50 px-3 py-1.5 rounded-md border border-sky-200"
          data-testid="admin-security-refresh"
        >
          <RefreshCw size={12} />{bg ? "Опресни" : "Refresh"}
        </button>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard testId="kpi-total-users" label={bg ? "Общо потребители" : "Total users"} value={u.total || 0} />
        <KpiCard testId="kpi-2fa-adoption" label={bg ? "2FA прием" : "2FA adoption"}
          value={`${u.twofa_adoption_pct ?? 0}%`} hint={`${u.twofa_enabled || 0} / ${u.total || 0}`} accent="emerald" />
        <KpiCard testId="kpi-consent" label={bg ? "С consent" : "With consent"}
          value={`${u.consent_recorded_pct ?? 0}%`} hint={`${u.consent_recorded || 0} / ${u.total || 0}`} accent="indigo" />
        <KpiCard testId="kpi-suspicious-30d" label={bg ? "Подозрителни входове (30д)" : "Suspicious logins (30d)"}
          value={l.suspicious || 0} hint={`${l.suspicious_pct ?? 0}% от ${l.total || 0}`} accent="amber" />
        <KpiCard testId="kpi-abuse-flagged" label={bg ? "Флагнати за злоупотреба" : "Abuse-flagged"}
          value={u.abuse_flagged || 0} accent="rose" />
      </div>

      {/* Two-column detail */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Top signup IPs */}
        <section className="rounded-xl border border-gray-200 bg-white p-4" data-testid="top-ips-section">
          <div className="flex items-center gap-2 mb-3">
            <UserX size={16} className="text-rose-600" />
            <h3 className="font-semibold text-gray-900 text-sm">
              {bg ? "Топ IP адреси по регистрации (24ч)" : "Top signup IPs (24h)"}
            </h3>
          </div>
          {(data.signup_abuse?.top_ips_24h || []).length === 0 ? (
            <div className="text-xs text-gray-500">{bg ? "Няма записи" : "No data"}</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {data.signup_abuse.top_ips_24h.map((x, i) => (
                <li key={x.ip || `ip-${i}`} className="flex items-center justify-between py-2 text-sm">
                  <span className="font-mono text-gray-700">{x.ip || "—"}</span>
                  <span className={`text-xs font-semibold ${x.count > 3 ? "text-rose-600" : "text-gray-600"}`}>
                    {x.count} {bg ? "регистрации" : "signups"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Rate-limit top buckets */}
        <section className="rounded-xl border border-gray-200 bg-white p-4" data-testid="top-buckets-section">
          <div className="flex items-center gap-2 mb-3">
            <Zap size={16} className="text-amber-600" />
            <h3 className="font-semibold text-gray-900 text-sm">
              {bg ? "Топ rate-limit buckets" : "Top rate-limit buckets"}
            </h3>
          </div>
          {(data.rate_limits?.top_buckets || []).length === 0 ? (
            <div className="text-xs text-gray-500">{bg ? "Няма активни прозорци" : "No active windows"}</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {data.rate_limits.top_buckets.map((b, i) => (
                <li key={b.bucket || `bucket-${i}`} className="flex items-center justify-between py-2 text-sm">
                  <div>
                    <div className="font-medium text-gray-900">{b.bucket}</div>
                    <div className="text-[11px] text-gray-500">
                      {b.windows} {bg ? "прозореца" : "windows"} · {bg ? "лимит" : "limit"}: {b.limit_per_window}
                    </div>
                  </div>
                  <span className="text-xs font-semibold text-amber-700">
                    {b.total_requests} {bg ? "заявки" : "req"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* Recent suspicious sessions */}
      <section className="rounded-xl border border-gray-200 bg-white p-4" data-testid="suspicious-sessions-section">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle size={16} className="text-rose-600" />
          <h3 className="font-semibold text-gray-900 text-sm">
            {bg ? "Последни подозрителни входове" : "Recent suspicious sessions"}
          </h3>
        </div>
        {(data.recent_suspicious_sessions || []).length === 0 ? (
          <div className="text-xs text-gray-500">{bg ? "Няма подозрителни входове през последните 30 дни" : "No suspicious logins in the last 30 days"}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50">
                <tr className="text-left text-gray-600">
                  <th className="py-2 px-2">{bg ? "Кога" : "When"}</th>
                  <th className="py-2 px-2">{bg ? "Потребител" : "User"}</th>
                  <th className="py-2 px-2">IP</th>
                  <th className="py-2 px-2 flex items-center gap-1"><Globe size={11} />{bg ? "Локация" : "Location"}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.recent_suspicious_sessions.map((s, i) => (
                  <tr key={`${s.created_at || ''}-${s.user_id || ''}-${s.ip || ''}-${i}`}>
                    <td className="py-1.5 px-2 text-gray-700">{s.created_at ? new Date(s.created_at).toLocaleString() : "—"}</td>
                    <td className="py-1.5 px-2 font-mono text-gray-500">{s.user_id?.slice(-8)}</td>
                    <td className="py-1.5 px-2 font-mono">{s.ip || "—"}</td>
                    <td className="py-1.5 px-2">{[s.city, s.country].filter(Boolean).join(", ") || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* NEW v2: Flagged IPs (abuse / brute-force) */}
      <section className="rounded-2xl border bg-white p-4" data-testid="admin-flagged-ips-section">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-base font-semibold flex items-center gap-2">
            <AlertTriangle size={16} className="text-rose-600" />
            {bg ? "Подозрителни IP-та (последни 7 дни)" : "Flagged IPs (last 7 days)"}
          </h2>
          <button onClick={loadAbuse} className="inline-flex items-center gap-1 text-xs text-sky-700 hover:underline" data-testid="admin-flagged-ips-refresh">
            <RefreshCw size={12} className={abuseLoading ? "animate-spin" : ""} />
            {bg ? "Обнови" : "Refresh"}
          </button>
        </div>
        {flaggedIps.length === 0 ? (
          <p className="text-xs text-gray-500">{bg ? "Няма подозрителни IP-та." : "No flagged IPs."}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50">
                <tr className="text-left text-gray-600">
                  <th className="py-2 px-2">IP</th>
                  <th className="py-2 px-2">{bg ? "Държава" : "Country"}</th>
                  <th className="py-2 px-2">{bg ? "Град" : "City"}</th>
                  <th className="py-2 px-2">ISP</th>
                  <th className="py-2 px-2 text-right">{bg ? "Грешки" : "Failures"}</th>
                  <th className="py-2 px-2 text-right">{bg ? "Блок." : "Blocked"}</th>
                  <th className="py-2 px-2 text-right">{bg ? "Цели" : "Targets"}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {flaggedIps.slice(0, 20).map((row) => (
                  <tr key={row.ip} data-testid={`admin-flagged-ip-${row.ip}`}>
                    <td className="py-1.5 px-2 font-mono">{row.ip}</td>
                    <td className="py-1.5 px-2">
                      {row.country_code && <span className="mr-1 text-[10px] px-1 py-0.5 bg-gray-100 rounded">{row.country_code}</span>}
                      {row.country || "—"}
                    </td>
                    <td className="py-1.5 px-2">{row.city || "—"}</td>
                    <td className="py-1.5 px-2 text-gray-600 truncate max-w-[180px]">{row.isp || "—"}</td>
                    <td className="py-1.5 px-2 text-right font-semibold text-rose-600">{row.failures}</td>
                    <td className="py-1.5 px-2 text-right">{row.blocks}</td>
                    <td className="py-1.5 px-2 text-right text-amber-600">{row.n_distinct_emails}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* NEW v2: Flagged Users */}
      <section className="rounded-2xl border bg-white p-4" data-testid="admin-flagged-users-section">
        <h2 className="text-base font-semibold flex items-center gap-2 mb-2">
          <UserX size={16} className="text-amber-600" />
          {bg ? "Атакувани потребители" : "Targeted users"}
        </h2>
        {flaggedUsers.length === 0 ? (
          <p className="text-xs text-gray-500">{bg ? "Няма атакувани акаунти." : "No targeted accounts."}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50">
                <tr className="text-left text-gray-600">
                  <th className="py-2 px-2">Email</th>
                  <th className="py-2 px-2 text-right">{bg ? "Опити" : "Attempts"}</th>
                  <th className="py-2 px-2 text-right">{bg ? "Уник. IP" : "Unique IPs"}</th>
                  <th className="py-2 px-2">{bg ? "Държави" : "Countries"}</th>
                  <th className="py-2 px-2">{bg ? "Градове" : "Cities"}</th>
                  <th className="py-2 px-2">{bg ? "Последно" : "Last"}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {flaggedUsers.slice(0, 20).map((row) => (
                  <tr key={row.email} data-testid={`admin-flagged-user-${row.email}`}>
                    <td className="py-1.5 px-2 font-mono text-xs">{row.email}</td>
                    <td className="py-1.5 px-2 text-right font-semibold text-rose-600">{row.failures}</td>
                    <td className="py-1.5 px-2 text-right">{row.n_distinct_ips}</td>
                    <td className="py-1.5 px-2">{(row.countries || []).join(", ") || "—"}</td>
                    <td className="py-1.5 px-2">{(row.cities || []).join(", ") || "—"}</td>
                    <td className="py-1.5 px-2 text-gray-600">{row.last_seen ? new Date(row.last_seen).toLocaleString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
